import { useEffect, useSyncExternalStore, type ReactNode } from "react";

import { accountFromKey } from "@/features/site-shell/navigation";
import { FeatureLink } from "@softmax/web-features/navigation";

export function AccountBackLink({
  href,
  remember = false,
  className = "",
  children,
  ...rest
}: {
  href: string;
  remember?: boolean;
  className?: string;
  children: ReactNode;
}) {
  const to = useSyncExternalStore(
    subscribeToStorage,
    () => (remember ? rememberedHref(href) : href),
    () => href,
  );
  useEffect(() => {
    if (
      remember &&
      sessionStorage.getItem(accountFromKey) === null &&
      to !== href
    ) {
      sessionStorage.setItem(accountFromKey, to);
    }
  }, [remember, to, href]);
  return (
    <FeatureLink
      href={to}
      className={`font-plex-sans -m-[8px] inline-flex items-center gap-[8px] self-start p-[8px] text-[16px] leading-[24px] text-[#5e5e5e] focus-visible:outline-2 focus-visible:outline-offset-[2px] focus-visible:outline-[#161616] motion-safe:transition-colors hover:text-[#161616] ${className}`}
      {...rest}
    >
      <svg
        aria-hidden="true"
        width="8"
        height="13"
        viewBox="0 0 8 13"
        fill="none"
        className="block shrink-0"
      >
        <path
          d="M6.5 0.75 1 6.36l5.5 5.61"
          stroke="currentColor"
          strokeWidth="1.3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      {children}
    </FeatureLink>
  );
}

function subscribeToStorage(onChange: () => void) {
  window.addEventListener("storage", onChange);
  return () => window.removeEventListener("storage", onChange);
}

function rememberedHref(fallback: string): string {
  const kept = sessionStorage.getItem(accountFromKey);

  if (kept !== null) return kept;

  if (!document.referrer) return fallback;

  const from = new URL(document.referrer);

  if (
    from.origin !== window.location.origin ||
    /^\/(account|sign-in)(\/|$)/.test(from.pathname)
  )
    return fallback;

  return from.pathname + from.search;
}
